<template>
  <section class="well should-you-call-final-conclusion" id="should-you-call-final-conclusion">
    <header>
      <h1 class="page-header">Subsequent analysis that factors in future streets</h1>
    </header>
    <iframe class="embed-responsive-item" width="560" height="315" src="https://www.youtube.com/embed/N7y0QRlt1dg?rel=0&amp;showinfo=0" frameborder="0" allow="autoplay; encrypted-media" allowfullscreen></iframe>
    <p>The initial analysis above should just be preliminary. Factoring in the possibility of betting on future streets is really important!</p>
    <p>What you really want to do when deciding whether or not to call is to estimate your real odds using ideas like implied odds, reverse implied odds, and effective odds. See the video above and the list below for help with that.</p>
    <p>Real odds:</p>
    <p>
      <input
        type="number"
        v-bind:value="realOddsLHS"
        v-on:input="$emit('setRealOddsLHS', $event.target.valueAsNumber)"
        min="0.1"
        step="0.1"
      >
      -to-1
    </p>
    <p>Breakeven percentage according to real odds: {{ breakevenPercentageViaRealOdds.toFixed(2) }}%</p>
    <div v-if="isCallProfitableBasedOnRealOdds" class="alert alert-success">
      According to the real odds that you estimated, your call <em>will</em> be profitable. ✅
    </div>
    <div v-else class="alert alert-danger">
      According to the real odds that you estimated, your call <em>will not</em> be profitable. ❌
    </div>
    <hr>
    <p>Reasons why it is important to factor in betting on future streets:</p>
    <ol>
      <li>
        <p>Effective odds: Suppose you have 30% equity with a flush draw and have <code>4:1</code> pot odds. The initial analysis says that you only need 25% equity to justify calling, and that it is worth calling because you have 30%. But what happens when you miss your flush on the turn and your opponent bets again?</p>
        <p>That 30% equity lied to you! It assumes that you deal to the river. But if you throw away your hand on the turn, you don't actually get to see the river.</p>
        <p>
          The <code>4:1</code> pot odds also lied to you. Suppose the pot is $40 and you had to call $10 on the flop. You can't say, "when I win, I win $40, and when I lose, I lose the $10". Your opponent is probably going to fire another $25 into the pot on the turn, so you have to think, "when I win I'm going to win the initial $40 plus the $25 my opponent throws in there on the turn, but when I lose I'm going to lose the initial $10 plus the $25 on the turn". When you think like that, you realize that your <em>effective odds</em> are approximately <code>13:7</code>, a hair less than <code>2:1</code>.
        </p>
      </li>
      <li>
        Implied odds: Taking the above example further, when you hit your flush, you don't just win your opponents normal flop and turn bets. You'll probably get another street of value from them, maybe by raising the turn, maybe by betting the river. So it is incorrect to think, "when I win, I win the inital $40 plus the $25 turn bet". You should think, "when I win, I'll win the initial $40, plus the $25 turn bet, <em>plus another $50 or so</em>". Which means that the <em>implied odds</em> you're getting are about <code>115:35</code>, or <code>3.3:1</code>.
      </li>
      <li>
        Reverse implied odds: What if you hit a hand that you think is good, like a weak flush, and your opponent has something better, like the nut flush? In that case you are going to lose future bets instead of win them.
      </li>
      <li>
        Having to fold: In the analysis of effective odds, we said that your opponent would be continuing on the turn and you'd be calling. but you probably aren't <em>always</em> going to be calling. Some turn cards are too scary to be continuing on. The fact that you are going to have to fold some times needs to be considered.
      </li>
    </ol>
    <p>The video further above expands on these ideas. So does the book <a href="https://www.twoplustwo.com/books/poker/theory-of-poker/">The Theory of Poker by David Sklansky</a> (a classic and a must-read).</p>
  </section>
</template>

<script>
import ToggleHelpButton from '@/components/guided-analyses/help/toggle-help-button';

export default {
  name: 'should-you-call-final-conclusion',
  components: {
    'toggle-help-button': ToggleHelpButton,
  },
  props: ['equity', 'potOdds', 'realOddsLHS', 'breakevenPercentageViaRealOdds'],
  data: function () {
    return {
      helpAreaVisible: {
        futureStreets: [false, false, false, false, false],
      },
    };
  },
  computed: {
    isCallProfitableBasedOnRealOdds: function () {
      return this.equity > this.breakevenPercentageViaRealOdds;
    },
  },
  watch: {
    potOdds: function (newPotOdds, oldPotOdds) {
      let split = newPotOdds.split(':');
      let lhs = Number(split[0]);
      let rhs = Number(split[1]);

      this.$emit('setRealOddsLHS', (lhs / rhs))
    },
  },
  methods: {
    toggleHelp: function (type, index) {
      if (type === 'futureStreets') {
        this.helpAreaVisible.futureStreets[index] = !this.helpAreaVisible.futureStreets[index];
      }

      this.$forceUpdate();
    },
  },
};
</script>

<style lang="scss" scoped>
  @import 'src/assets/help-section';

  .should-you-call-final-conclusion {
    font-size: 18px;

    pre {
      font-size: 16px;
    }

    input[type=number].form-control {
      font-size: 18px;
    }

    h1, h3, li, p, table {
      max-width: 800px;
    }

    .well {
      margin-top: 10px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/guided-analyses/should-you-call/conclusions/final-conclusion.vue